import { MeshProperties, Mesh, MeshFields } from "@/types/core/Mesh";
import { Vector3 } from "three";
import * as THREE from "three";
import { Tetrahedron } from "@react-three/drei";
import { forwardRef, useMemo } from "react";
import { TransformableItemProperties } from '../../core/Item';
import { Euler } from "three";
import { useAppContext } from "@/context/AppContext";
import { Material } from "@/types/core/Material";

export interface TetrahedronMeshProperties extends TransformableItemProperties {
  radius: number;
  detail: number;
  materialId?: string;
}

export interface TetrahedronMesh extends Mesh<TetrahedronMeshProperties> {
  subtype: 'tetrahedron';
  properties: TetrahedronMeshProperties;
}

// Create a reusable Tetrahedron component that can be used in both preview and world
const TetrahedronComponent = forwardRef<THREE.Mesh, { properties: TetrahedronMeshProperties; isPreview?: boolean }>((props, ref) => {
  const { properties, isPreview } = props;
  const { radius, detail, transform, materialId } = properties;
  const { activeWorld } = useAppContext();

  // In preview mode, use simplified transform
  const position: [number, number, number] = isPreview 
    ? [0, 0, 0] 
    : [transform.position.x, transform.position.y, transform.position.z];
  const rotation: [number, number, number] = isPreview
    ? [0, 0, 0]
    : [transform.rotation.x, transform.rotation.y, transform.rotation.z];
  const scale: [number, number, number] = isPreview
    ? [1, 1, 1]
    : [transform.scale.x, transform.scale.y, transform.scale.z];

  // Get the material from the world if available
  const materialItem = activeWorld && materialId ? activeWorld.items[materialId] : undefined;
  const material = materialItem?.type === 'material' ? (materialItem as unknown as Material) : undefined;

  // Create the material using the render function
  const meshMaterial = useMemo(() => {
    if (material) {
      return material.render(material.properties, isPreview);
    }
    return new THREE.MeshStandardMaterial({ color: "#808080" });
  }, [material, material?.properties, isPreview]);

  return (
    <Tetrahedron
      ref={ref}
      args={[radius, detail]}
      position={position}
      rotation={rotation}
      scale={scale}
      material={meshMaterial}
    />
  );
});

export const TetrahedronMeshItem: TetrahedronMesh = {
  id: '',
  name: 'Tetrahedron',
  type: 'mesh',
  subtype: 'tetrahedron',
  icon: 'Tetrahedron',
  properties: {
    radius: 1,
    detail: 0,
    materialId: '',
    transform: {
      position: new Vector3(),
      rotation: new Euler(),
      scale: new Vector3(1, 1, 1)
    }
  },
  fields: [
    ...MeshFields,
    {
      key: 'properties.radius',
      type: 'number',
      label: 'Radius',
      default: 1,
      panel: 'geometry'
    },
    {
      key: 'properties.detail',
      type: 'number',
      label: 'Detail',
      default: 0,
      panel: 'geometry'
    }
  ],
  render: (properties: TetrahedronMeshProperties, isPreview: boolean, ref: React.RefObject<THREE.Object3D | null>) => {
    return <TetrahedronComponent properties={properties} isPreview={isPreview} ref={ref as React.RefObject<THREE.Mesh>} />;
  }
}; 